import React, { useEffect, useState } from 'react';
import { OptimizelyExperiment, OptimizelyProvider, OptimizelyVariation, ReactSDKClient } from '@optimizely/react-sdk';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { ProPageStrings } from 'react_components/mixins/shared/string_constants_mixin';
import CheckCircleIcon from 'images/pro/greenCheck-filled.svg';
import { initializeOptimizely, trackOptimizelyEvent } from 'utils/OptimizelyHelpers';
import BulletList from 'components/shared/text/BulletList';
import { logSignUpPageViewed, logTrailPageViewLimitReached } from '@alltrails/analytics/amplitudeFunctions';
import Wrapper from '../Wrapper';
import * as baseStyles from '../../../styles/baseStyles.module.scss';
import * as styles from './styles/styles.module.scss';
import RegistrationForm from '../RegistrationForm';

const getText = (isProPage: boolean) => {
  const { registrationText, loginForm, formText, altLoginsText } = ProPageStrings;
  let text = {
    ...registrationText,
    ...loginForm,
    ...formText,
    ...altLoginsText
  };

  if (isProPage) {
    text = { ...text, ...ProPageStrings };
  }

  return text;
};

const handleOptimizelyEvent = (optimizelyInstance: ReactSDKClient, optimizelyUserId: any, eventType: any, eventOutcome: string, eventAttributes: {}) => {
  if (optimizelyInstance) {
    trackOptimizelyEvent(optimizelyInstance, optimizelyUserId, eventType, eventOutcome, eventAttributes);
  }
};

interface FormFooterProps {
  text?: {
    and: string;
    privacyPolicyLead?: string;
    termsLink?: string;
    privacy?: string;
  };
}

const FormFooter = ({ text }: FormFooterProps) => (
  <div className={baseStyles.info}>
    {text.privacyPolicyLead}{' '}
    <a className={baseStyles.route} href="/terms">
      {text.termsLink}
    </a>{' '}
    {text.and}{' '}
    <a className={baseStyles.route} href="/privacy">
      {text.privacy}
    </a>
    .
  </div>
);

interface OptimizelyMockSignupPageTestProps {
  optimizelyInstance: any;
}

const OptimizelyMockSignupPageTest = ({ optimizelyInstance }: OptimizelyMockSignupPageTestProps) => {
  if (!optimizelyInstance) {
    return null;
  }

  const featuresList = ['Keep track of your favorite trails', 'Record and share your activity', 'Create and share custom maps'];

  return (
    <OptimizelyProvider optimizely={optimizelyInstance} isServerSide>
      <OptimizelyExperiment experiment="mock_signup_page_test">
        <OptimizelyVariation variation="variation_description">
          <BulletList
            bullet={<img alt="check-circle-icon" src={CheckCircleIcon} style={{ color: '#428a13' }} />}
            classes={styles.bulletList}
            list={featuresList}
          />
        </OptimizelyVariation>
        <OptimizelyVariation variation="variation_control" />
        <OptimizelyVariation default />
      </OptimizelyExperiment>
    </OptimizelyProvider>
  );
};

interface SignUpFormProps {
  isProPage?: boolean;
  mobileBrowser?: boolean;
  optimizelySettings?: {datafile: string};
  countryId: number;
  handleRegistrationSuccess?(...args: unknown[]): unknown;
  redirectTo?: string;
  handleLoginClick?(...args: unknown[]): unknown;
  enableRecaptcha: boolean;
  facebookAppId: number;
  referralLink?: string;
}

const SignUpForm = ({
  isProPage,
  mobileBrowser,
  optimizelySettings,
  countryId,
  handleRegistrationSuccess,
  redirectTo,
  handleLoginClick,
  enableRecaptcha,
  facebookAppId,
  referralLink
}: SignUpFormProps) => {
  const languageRegionCode = useLanguageRegionCode();
  const [optimizelyInstance, updateOptimizelyInstance] = useState(null);
  const [optimizelyUserId, updateOptimizelyUserId] = useState(null);
  
  useEffect(() => {
    const [_optimizelyInstance, _optimizelyUserId] = initializeOptimizely(optimizelySettings);
    if (_optimizelyInstance) {
      const optimizelyAttributes = { locale: languageRegionCode, mobileBrowser };
      _optimizelyInstance.setUser({ id: _optimizelyUserId, attributes: optimizelyAttributes });
      updateOptimizelyInstance(_optimizelyInstance);
      updateOptimizelyUserId(_optimizelyUserId);
    }
  }, []);

  useEffect(() => {
    logSignUpPageViewed();

    const params = new URLSearchParams(window.location.search);

    if (params.has('limit') && params.has('trail_id')) {
      logTrailPageViewLimitReached({ limit: parseInt(params.get('limit')), trail_id: parseInt(params.get('trails_id')) });
      params.delete('limit');
      params.delete('trail_id');
      const search = params.toString();
      window.history.replaceState(null, null, window.location.pathname + (search !== '' ? `?${search}` : ''));
    }
  }, []);

  const text = getText(isProPage);
  const formFooter = <FormFooter text={text} />;

  return (
    <Wrapper text={text} foot={formFooter}>
      <OptimizelyMockSignupPageTest optimizelyInstance={optimizelyInstance} />
      <RegistrationForm
        text={text}
        countryId={countryId}
        handleRegistrationSuccess={handleRegistrationSuccess}
        handleLoginClick={handleLoginClick}
        enableRecaptcha={enableRecaptcha}
        facebookAppId={facebookAppId}
        redirectTo={redirectTo}
        handleOptimizelyEvent={(eventType: string, eventOutcome: string, eventAttributes: {}) =>
          handleOptimizelyEvent(optimizelyInstance, optimizelyUserId, eventType, eventOutcome, eventAttributes)
        }
        referralLink={referralLink}
        userEmailSubscribed
      />
    </Wrapper>
  );
};

SignUpForm.defaultProps = {
  isProPage: false,
  handleRegistrationSuccess: () => {},
  handleLoginClick: null,
  redirectTo: null,
  mobileBrowser: null,
  optimizelySettings: null,
  referralLink: null
};

FormFooter.defaultProps = {
  text: {
    privacyPolicyLead: 'Privacy Policy',
    termsLink: '/terms',
    privacy: 'Privacy'
  }
};

export default SignUpForm;
